import { TransformsList, Data } from 'cio/utils/analytics/transforms';
import { generateSource } from 'cio/utils/analytics/sources';

export type IntegrationSnippetType = 'js_snippet' | 'js_identify' | 'api_identify' | 'api_track';
export type IntegrationSource = 'js' | 'api' | 'csv' | 'segment';

const INTEGRATION_NAME_OVERRIDES: Record<string, string> = {
  'Amazon Redshift': 'Redshift',
  'Amazon S3': 'S3',
};

const mixpanelIntegrationName = (name: string) => INTEGRATION_NAME_OVERRIDES[name] || name;

const Transforms: TransformsList = {
  listViewed() {
    return {
      Source: 'In-App',
    };
  },

  listViewedPublic() {
    return {
      Source: 'Public',
    };
  },

  click(data) {
    let attrs: { [key: string]: string } = {
      Source: data.from,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Category': data.category,
      'Integration Sub-Category': data.subtitle,
      'Integration Type': data.type,
    };

    // Only send integration status when it is actually passed in.
    if ([false, true].includes(data.enabled)) {
      attrs['Integration Status'] = data.enabled ? 'Enabled' : 'Disabled';
    }

    return attrs;
  },

  slackSettingsView(data) {
    return { 'Slack Status': data.enabled ? 'Enabled' : 'Disabled' };
  },

  eventWithSource(data: Data) {
    return { Source: generateSource(data) };
  },

  twilioSettingsView(data) {
    return { 'Twilio SMS Status': data.enabled ? 'Enabled' : 'Disabled' };
  },

  gistSettingsView(data) {
    return { 'Gist In-App Messaging Status': data.enabled ? 'Enabled' : 'Disabled' };
  },

  // One-off transformation specifically for when Segment integration is
  // enabled. Usually these events are sent through the backend, but in this
  // case, the backend does not know when it is enabled.
  segmentEnabled() {
    return {
      'Integration Name': 'Segment',
      'Integration Category': 'Data management',
      'Integration Sub-Category': 'Destination',
    };
  },

  requestStarted(data) {
    return {
      Source: data.from,
      'Integration Request Type': data.requestType,
      'Integration Category': data.category,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Type': data.type,
    };
  },

  requestCompleted(data) {
    let priceFloat = parseFloat(data.price);

    return {
      Source: data.from,
      'Integration Request Type': data.requestType,
      'Integration Type': data.type,
      'Integration Category': data.category,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Functionality': data.functionality,
      'Integration Outcome': data.outcome,
      'Integration WTP': data.wouldPay,
      'Integration WTP Price': isNaN(priceFloat) ? data.price : priceFloat,
      'Integration Comments': data.comments,
      'Integration Request Author Email': data.userEmail,
      'Integration Number of Contacts': data.contacts,
      'Live Forms Today?': data.liveFormsToday,
    };
  },

  landingPageButtonClicked(data) {
    return {
      Source: data.from,
      'Integration Category': data.category,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Type': data.type,
      'Integration Clicked On': data.clickedOn,
    };
  },

  requestSchedulingStarted(data) {
    return {
      Source: data.from,
      'Integration Request Type': data.requestType,
      'Integration Type': data.type,
      'Integration Category': data.category,
      'Integration Name': mixpanelIntegrationName(data.name),
    };
  },

  listSearchedOrFiltered(data) {
    return {
      Source: data.from,
      'List Searched': !!data.query,
      'List Filtered by Category': !!data.category,
      'List Filtered by Type': !!data.type,
      'Integration Category': data.category,
      'Integration Type': data.type,
      'Integration Search Value': data.query,
    };
  },

  notifyWhenAvailableClicked(data) {
    return {
      Source: data.from,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Category': data.category,
      'Integration Type': data.type,
      'Integration Request Author Email': data.userEmail,
    };
  },

  useCaseLinkClicked(data) {
    return {
      Source: data.from,
      'Integration Category': data.category,
      'Integration Name': mixpanelIntegrationName(data.name),
      'Integration Type': data.type,
      'Integration Link URL': data.url,
    };
  },

  keyType(data) {
    return { 'API Credential Type': data.keyType };
  },

  apiCredentialCopied(data) {
    return {
      'API Credential Type': data.credentialType,
      'API Credential Copied': data.credentialCopied,
      Source: data.source,
    };
  },

  enableStarted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
      'Integration Enabled or Re-Enabled': data.enabledOrReenabled,
      Source: data.source,
    };
  },

  enabled(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
      'Integration Enabled or Re-Enabled': data.enabledOrReenabled,
    };
  },

  verifyStarted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
    };
  },

  verifyCompleted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
      'Integration Verify Status': data.status,
    };
  },

  disableStarted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
      'Integration Disabled or Deleted': data.disabledOrDeleted,
      'Webhook Endpoint ID': data.endpointId,
      'Webhook Endpoint Disabled or Deleted': data.endpointDisabledOrDeleted,
      Source: data.source,
    };
  },

  disabled(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
      'Integration Disabled or Deleted': data.disabledOrDeleted,
    };
  },

  clearStarted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
    };
  },

  clearCompleted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Integration Sub-Category': data.subCategory,
    };
  },

  reportingWebhookTestSent(data) {
    return {
      'Test Passed': data.success,
      'Test Response': data.response,
      'Webhook Endpoint ID': data.endpointId,
    };
  },

  reportingWebhookKeyCopied(data) {
    return {
      'Webhook Endpoint ID': data.endpointId,
      Source: data.source,
    };
  },

  editStarted(data) {
    return {
      'Integration Name': data.name,
      'Integration Category': data.category,
      'Webhook Endpoint ID': data.endpointId,
      Source: data.source,
    };
  },

  errorEncountered(data) {
    return {
      'Integration Name': data.name,
      'Integration Step': data.step,
      Error: data.error,
      Source: data.source,
    };
  },
  dataCampaignLinkClicked(data) {
    if (data.destination != null) {
      return {
        Example: data.destination,
      };
    }

    return {};
  },
};

export default Transforms;
